/** Batch jobs API (spec: docs/superpowers/specs/2026-10-16-batch-jobs-design.md). */
import { apiFetch } from "@/auth/token";
import { friendlyError } from "@/api/friendlyError";

export type BatchStatus =
  | "draft" | "classifying" | "running" | "awaiting_review" | "cancelling"
  | "completed" | "completed_with_errors" | "failed" | "cancelled";
export type BatchItemStatus =
  | "queued" | "needs_review" | "running" | "succeeded"
  | "failed" | "cancelled" | "skipped";

export interface Batch {
  id: string;
  name: string;
  mode: "auto" | "explicit";
  type: string | null;
  status: BatchStatus | string;
  total: number;
  succeeded: number;
  failed: number;
  held: number;
  cancelled: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  has_results: boolean;
  error?: string | null;
}

export interface BatchItem {
  id: string;
  seq: number;
  filename: string;
  kind: "invoice" | "contract" | "other" | "unknown";
  handler: string | null;
  confidence: number;
  role: "primary" | "context";
  /** The stored input document; lets the review UI offer this file as a contract. */
  document_id?: number | null;
  contract_document_id: number | null;
  contract_filename?: string | null;
  pair_source: "upload" | "project_docs" | "none";
  status: BatchItemStatus | string;
  summary?: {
    red_flags?: number;
    yellow_flags?: number;
    confirmed_dollar_impact?: number;
    audit_status?: string;
  };
  error?: string | null;
}

export interface BatchDetail { batch: Batch; items: BatchItem[] }

export interface BatchCreateInput {
  files: File[];
  mode: "auto" | "explicit";
  type?: string;
  /** contract_mode, skill name/prompt, ... serialized as a JSON string. */
  params?: Record<string, unknown>;
  name?: string;
}

export interface BatchItemPatch {
  handler?: string | null;
  contract_document_id?: number | null;
  exclude?: boolean;
}

/** Statuses in which the list should keep polling. */
export const ACTIVE_BATCH_STATUSES = ["classifying", "running", "cancelling"];
export const TERMINAL_BATCH_STATUSES = ["completed", "completed_with_errors", "failed", "cancelled"];

async function jsonOk<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(friendlyError(res.status));
  return res.json() as Promise<T>;
}

/** Cookie-session request init; identity rides the HttpOnly cookie. */
function reqInit(extra?: RequestInit): RequestInit {
  return {
    credentials: "include",
    ...extra,
    headers: { ...(extra?.headers ?? {}) },
  };
}

async function postOk(path: string): Promise<void> {
  const r = await apiFetch(path, reqInit({ method: "POST" }));
  if (!r.ok) throw new Error(friendlyError(r.status));
}

export async function listBatches(): Promise<Batch[]> {
  const r = await apiFetch(`/v1/batches`, reqInit());
  return (await jsonOk<{ batches: Batch[] }>(r)).batches;
}

export async function getBatch(id: string): Promise<BatchDetail> {
  const r = await apiFetch(`/v1/batches/${id}`, reqInit());
  return jsonOk<BatchDetail>(r);
}

/** Multipart: the browser sets the Content-Type boundary, so never set it. */
export async function createBatch(input: BatchCreateInput): Promise<BatchDetail> {
  const fd = new FormData();
  for (const f of input.files) fd.append("files", f);
  fd.append("mode", input.mode);
  if (input.type) fd.append("type", input.type);
  if (input.params) fd.append("params", JSON.stringify(input.params));
  if (input.name) fd.append("name", input.name);
  const r = await apiFetch(`/v1/batches`, reqInit({ method: "POST", body: fd }));
  return jsonOk<BatchDetail>(r);
}

export async function patchBatchItem(
  batchId: string, itemId: string, patch: BatchItemPatch,
): Promise<BatchItem> {
  const r = await apiFetch(
    `/v1/batches/${batchId}/items/${itemId}`,
    reqInit({
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }),
  );
  return jsonOk<BatchItem>(r);
}

export const startBatch = (id: string) => postOk(`/v1/batches/${id}/start`);
export const retryFailedBatch = (id: string) => postOk(`/v1/batches/${id}/retry-failed`);
export const cancelBatch = (id: string) => postOk(`/v1/batches/${id}/cancel`);

export async function getItemReport(batchId: string, itemId: string): Promise<string> {
  const r = await apiFetch(`/v1/batches/${batchId}/items/${itemId}/report`, reqInit());
  const body = await jsonOk<{ report: string }>(r);
  return body.report;
}

/** Fetch the zip with the session cookie (a bare link would work too, but a
 *  blob lets us surface errors instead of navigating to a JSON 4xx). */
export async function downloadBatchZip(id: string): Promise<Blob> {
  const r = await apiFetch(`/v1/batches/${id}/download`, reqInit());
  if (!r.ok) throw new Error(friendlyError(r.status));
  return r.blob();
}

/** Trigger a browser save of the batch's results.zip. */
export async function saveBatchZip(id: string, name: string): Promise<void> {
  const blob = await downloadBatchZip(id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name.replace(/[^\w.-]+/g, "_") || "batch"}-results.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking synchronously after click() can abort the download in
  // Firefox/Safari, which start reading the blob asynchronously.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
